import { IAngularEvent } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";
import dayjs from "@gtmhub/dayjs";

import { IInsight, IInsightCalculationTransaction } from "../../../models/models";
import { IParameter } from "@gtmhub/parameters/models";
import { InsightService } from "../../../services/insightService";
import { ParameterService } from "@gtmhub/parameters/parameterService";
import {
  IDynamicItemSettings,
  IExecuteInsightContext,
  IUpdateInsightSelectorDataActionParams,
  IBrowseInsightsActionParams,
  IBrowseConnectionsActionsParams,
  IEditFiltersContext,
  IStringMap,
  IDateRangePicker,
  IMultiselectMap,
  ITimePeriod,
  IInsightResponseMap,
} from "../models";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { ICollection } from "@gtmhub/models";
import { byTextField } from "@gtmhub/util/sorting";
import { isNumberRounded, toFixedWithoutRounding } from "@gtmhub/shared/utils";
import { isPeriodString, convertPeriodStringToObject, convertDateRangeObjectToString } from "../../../util";
import { IInsightCalculationResultMessage } from "@gtmhub/sockets";

export interface IExecuteInsightScope extends IModalScope {
  calculatedInsightValue: number;
  error: string;
  indicators: {
    executing: boolean;
    getting: boolean;
  };
  insight: IInsight;
  insightFilters: IParameter[];
  insightTransactionId: string;
  datePickers: IStringMap;
  dateRangePickers: IDateRangePicker;
  multiselects: IMultiselectMap;
  persistedFilters: IStringMap;
  persistedFilterValueDisplayNames: IStringMap;
  selectedFilters: IStringMap;
  selectedFilterValueDisplayNames: IStringMap;
  calculate(): void;
  confirm(): void;
  goBack(): void;
  isPeriodString(value: string): boolean;
  toggleFilterSelector(filter?: IParameter): void;
}

export class ExecuteInsightCtrl {
  private fieldName: string;

  public static $inject = ["$uibModal", "$scope", "InsightService", "ParameterService", "executeInsightContext", "UIErrorHandlingService"];

  constructor(
    private $uibModal: IModalService,
    private $scope: IExecuteInsightScope,
    private insightService: InsightService,
    private parameterService: ParameterService,
    private executeInsightContext: IExecuteInsightContext,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.indicators = {
      executing: false,
      getting: true,
    };

    this.$scope.calculate = this.calculate;
    this.$scope.confirm = this.confirm;
    this.$scope.goBack = this.goBack;
    this.$scope.isPeriodString = this.isPeriodString;
    this.$scope.toggleFilterSelector = this.toggleFilterSelector;
    this.$scope.$on("insightResponse", this.onInsightResponse);

    this.init();
  }

  private insightResponseMap: IInsightResponseMap = {};

  private init = () => {
    this.$scope.insight = this.executeInsightContext.insight;
    this.fieldName = this.executeInsightContext.data?.fieldName ? this.executeInsightContext.data.fieldName : null;

    this.parameterService.getParameters().then(
      (parameters: ICollection<IParameter>) => {
        this.$scope.indicators.getting = false;
        const insightFilters = this.insightService.getInsightUsedFilters(this.$scope.insight, parameters.items);
        this.$scope.insightFilters = insightFilters.sort(byTextField("title"));
        this.processFilters();
        this.calculate();
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "getting" })
    );
  };

  private calculate = () => {
    this.$scope.indicators.executing = true;
    const parameters = this.getFilterSettings();

    this.$scope.insightTransactionId = null;
    this.insightService.requestInsightCalculationById(this.$scope.insight.id, parameters).then(
      (result: IInsightCalculationTransaction) => {
        // Note: transaction id is too slow to return and usually WS message arrives first
        // => no calculated value to display because transactionId's don't match
        this.$scope.insightTransactionId = result.transactionId;
        this.checkInsightResponseMap();

        if (this.$scope.error) {
          // Make sure to clear previous error(s) in case there were some as this would hide the "Looks correct" button
          delete this.$scope.error;
        }
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "executing" })
    );
  };

  private checkInsightResponseMap = (): void => {
    const response = this.insightResponseMap[this.$scope.insightTransactionId];

    if (response) {
      if (response.status === "error") {
        this.$scope.error = response.message;
        const error: IUIError = {
          body: response.message,
        };

        this.uiErrorHandlingService.handleIndicator(error, { indicator: "executing", inline: false });
      } else {
        this.$scope.indicators.executing = false;

        let number: number;
        if (this.fieldName) {
          number = response.data[this.fieldName] as number;
        } else {
          [this.fieldName, number] = Object.entries(response.data)[0] as [string, number];
        }

        this.$scope.calculatedInsightValue = number;

        if (!isNumberRounded(number)) {
          this.$scope.calculatedInsightValue = toFixedWithoutRounding(number);
        }
      }
    }
  };

  private confirm = () => {
    const params: IUpdateInsightSelectorDataActionParams = {
      action: "updateInsightSelectorData",
      settings: this.getFilterSettings(),
      insight: this.$scope.insight,
      calculatedValue: this.$scope.calculatedInsightValue,
      fieldName: this.fieldName,
    };

    this.$scope.$close(params);
  };

  private getFilterSettings = (): IDynamicItemSettings[] => {
    const parameters: IDynamicItemSettings[] = [];

    Object.keys(this.$scope.selectedFilters).forEach((filterKey) => {
      if (!this.isFilterAvailableInInsight(filterKey)) {
        return;
      }

      const parameter: IDynamicItemSettings = {
        name: filterKey,
        title: this.getFilterTitle(filterKey),
        value: this.$scope.selectedFilters[filterKey],
        valueDisplayName: this.$scope.selectedFilterValueDisplayNames[filterKey],
        type: this.getFilterType(filterKey),
      };

      parameters.push(parameter);
    });

    // sort in alphabetic order
    return parameters.sort(byTextField("title"));
  };

  private getFilterTitle = (key: string): string => {
    const insightFilter = this.$scope.insightFilters.find((filter) => filter.key === key);

    return insightFilter ? insightFilter.title : null;
  };

  private getFilterType = (key: string): string => {
    const insightFilter = this.$scope.insightFilters.find((filter) => filter.key === key);

    return insightFilter ? insightFilter.selectorType : null;
  };

  private goBack = () => {
    const canLoadConnectionInsights = this.$scope.insight.connection && this.$scope.insight.connection.name;

    if (canLoadConnectionInsights) {
      const params: IBrowseInsightsActionParams = {
        action: "browseInsights",
        connectionData: this.$scope.insight.connection,
      };

      this.$scope.$close(params);
    } else {
      const params: IBrowseConnectionsActionsParams = {
        action: "browseConnections",
      };

      this.$scope.$close(params);
    }
  };

  private isFilterAvailableInInsight = (key: string): boolean => {
    return this.$scope.insightFilters.some((filter) => filter.key === key);
  };

  private isPeriodString = (value: string) => {
    return isPeriodString(value);
  };

  private onInsightResponse = (ev: IAngularEvent, response: IInsightCalculationResultMessage): void => {
    if (response && response.transactionID && response.data) {
      this.insightResponseMap[response.transactionID] = response.data;
    }
    this.checkInsightResponseMap();
  };

  private processFilters = (): void => {
    const settings = this.executeInsightContext.data && this.executeInsightContext.data.settings ? this.executeInsightContext.data.settings : [];

    // setup the persisted filters (ones already saved) and initial state of selectedFilters (those not yet saved)
    this.$scope.persistedFilters = {};
    this.$scope.persistedFilterValueDisplayNames = {};

    this.$scope.selectedFilters = {};
    this.$scope.selectedFilterValueDisplayNames = {};

    // setup additional filter maps used in the view
    this.$scope.multiselects = {};
    this.$scope.dateRangePickers = {};
    this.$scope.datePickers = {};

    // here we build the persisted filters from metric settings (if any filters already persisted in metric settings)
    // if metric settings contain persisted filters that are no longer available in insight => not displayed
    for (const filter of settings) {
      if (!this.isFilterAvailableInInsight(filter.name)) {
        continue;
      }

      this.$scope.persistedFilters[filter.name] = filter.value;
      this.$scope.persistedFilterValueDisplayNames[filter.name] = filter.valueDisplayName ? filter.valueDisplayName : filter.value;
    }

    // here we build the persisted and selected filters from insights used filters
    for (const parameter of this.$scope.insightFilters) {
      switch (parameter.selectorType) {
        case "text":
          this.setTextFilter(parameter);
          break;
        case "list":
          this.setListFilter(parameter);
          break;
        case "multiselect":
          this.setMultiselectFilter(parameter);
          break;
        case "daterange":
        // fall through
        case "systemdaterange":
          this.setDateRangeFilter(parameter);
          break;
        case "datepicker":
          this.setDatePickerFilter(parameter);
          break;
        default:
          // do nothing
          break;
      }
    }
  };

  private setDatePickerFilter = (parameter: IParameter) => {
    const defaultValue = parameter.defaultValue;
    const key = parameter.key;

    this.$scope.datePickers[key] = "";
    const persistedValue = this.$scope.persistedFilters[key];
    const hasPersistedValue: boolean = persistedValue !== null;

    if (hasPersistedValue) {
      this.$scope.datePickers[key] = persistedValue;
      this.$scope.selectedFilters[key] = persistedValue;
    } else if (defaultValue) {
      this.$scope.datePickers[key] = defaultValue;
      this.$scope.persistedFilters[key] = defaultValue;
      this.$scope.selectedFilters[key] = defaultValue;
    } else {
      this.$scope.persistedFilters[key] = "";
      this.$scope.selectedFilters[key] = "";
    }
  };

  private setDateRangeFilter = (parameter: IParameter) => {
    const defaultValue = parameter.defaultValue;
    const key = parameter.key;
    const defaultDateRange: ITimePeriod = {
      startDate: dayjs().format("YYYY-MM-DD"),
      endDate: dayjs().add(1, "M").format("YYYY-MM-DD"),
    };

    this.$scope.dateRangePickers[key] = defaultDateRange;
    const persistedValue = this.$scope.persistedFilters[key];
    const hasPersistedValue: boolean = persistedValue !== null;

    if (hasPersistedValue) {
      if (isPeriodString(persistedValue)) {
        this.$scope.dateRangePickers[key] = convertPeriodStringToObject(persistedValue);
      }

      this.$scope.selectedFilters[key] = persistedValue;
    } else if (defaultValue) {
      if (isPeriodString(defaultValue)) {
        this.$scope.dateRangePickers[key] = convertPeriodStringToObject(defaultValue);
      }

      this.$scope.persistedFilters[key] = defaultValue;
      this.$scope.selectedFilters[key] = defaultValue;
    } else {
      this.$scope.dateRangePickers[key] = defaultDateRange;
      this.$scope.persistedFilters[key] = convertDateRangeObjectToString(defaultDateRange);
      this.$scope.selectedFilters[key] = convertDateRangeObjectToString(defaultDateRange);
    }
  };

  private setListFilter = (parameter: IParameter) => {
    // if there isn't a record for this picker but the filter has default value we set it.
    const defaultValue = parameter.defaultValue;
    const key = parameter.key;
    const persistedValue = this.$scope.persistedFilters[key];
    const persistedValueDisplayNames = this.$scope.persistedFilterValueDisplayNames[key];
    const hasPersistedValue: boolean = persistedValue !== null && typeof persistedValue !== "undefined";

    if (hasPersistedValue) {
      this.$scope.selectedFilters[key] = persistedValue;
      this.$scope.selectedFilterValueDisplayNames[key] = persistedValueDisplayNames;
    } else if (defaultValue) {
      this.$scope.persistedFilters[key] = defaultValue;
      this.$scope.selectedFilters[key] = defaultValue;
    } else {
      this.$scope.persistedFilters[key] = "";
      this.$scope.selectedFilters[key] = "";
    }
  };

  private setMultiselectFilter = (parameter: IParameter) => {
    const defaultValues = parameter.defaultValue;
    const key = parameter.key;
    const multiselect = {
      selection: [],
      titles: [],
    };

    const persistedValues = this.$scope.persistedFilters[key];
    const persistedValueDisplayNames = this.$scope.persistedFilterValueDisplayNames[key];
    const hasPersistedValues: boolean = persistedValues !== null;

    if (hasPersistedValues) {
      if (persistedValues) {
        multiselect.selection = persistedValues.split(",");
      }

      if (persistedValueDisplayNames) {
        multiselect.titles = persistedValueDisplayNames.split(",");
      }

      this.$scope.selectedFilters[key] = persistedValues;
      this.$scope.selectedFilterValueDisplayNames[key] = persistedValueDisplayNames;
    } else if (defaultValues) {
      // TODO: defaultValue should be a json with selection and title properties
      // s.t. we can properly show the default value in the view
      // if there isn't a record for this picker but the filter has default value we set it.
      multiselect.selection = defaultValues.split(",");
      multiselect.titles = defaultValues.split(",");

      this.$scope.persistedFilters[key] = defaultValues;
      this.$scope.persistedFilterValueDisplayNames[key] = defaultValues;

      this.$scope.selectedFilters[key] = defaultValues;
      this.$scope.selectedFilterValueDisplayNames[key] = defaultValues;
    } else {
      this.$scope.persistedFilters[key] = "";
      this.$scope.selectedFilters[key] = "";
    }

    this.$scope.multiselects[key] = multiselect;
  };

  private setTextFilter = (parameter: IParameter) => {
    const defaultValue = parameter.defaultValue;
    const key = parameter.key;

    const persistedValue = this.$scope.persistedFilters[key];
    const hasPersistedValue: boolean = persistedValue !== null;

    if (hasPersistedValue) {
      this.$scope.selectedFilters[key] = persistedValue;
    } else if (defaultValue) {
      this.$scope.selectedFilters[key] = defaultValue;
      this.$scope.persistedFilters[key] = defaultValue;
    } else {
      this.$scope.persistedFilters[key] = "";
      this.$scope.selectedFilters[key] = "";
    }
  };

  private toggleFilterSelector = (filter?: IParameter) => {
    this.$uibModal
      .open({
        templateUrl: require("../edit-filters/edit-filters.jade"),
        windowClass: "medium-small filter-selector-modal",
        resolve: {
          editFiltersContext: (): IEditFiltersContext => ({
            filter,
            datePickers: this.$scope.datePickers,
            dateRangePickers: this.$scope.dateRangePickers,
            multiselects: this.$scope.multiselects,
            persistedFilters: this.$scope.persistedFilters,
            persistedFilterValueDisplayNames: this.$scope.persistedFilterValueDisplayNames,
            selectedFilters: this.$scope.selectedFilters,
            selectedFilterValueDisplayNames: this.$scope.selectedFilterValueDisplayNames,
          }),
        },
        controller: "EditFiltersCtrl",
      })
      .result.then(() => this.calculate());
  };
}
